import { last, isEmpty, reduce, split, init } from 'ramda';
import {
  addHours,
  addMinutes,
  format,
  getDay,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  roundToNearestMinutes,
  setHours,
  setMinutes,
  setSeconds
} from 'date-fns';
import { de, enUS, fr, frCH, it, es, pt, ptBR, hu, hr, sr, ro, sl, sk, cs, pl, ca, eu, gl, el } from 'date-fns/locale';

export const localeMap = {
  en: enUS,
  fr,
  'fr-CH': frCH,
  it,
  es,
  de,
  pt,
  'pt-BR': ptBR,
  hu,
  hr,
  sr,
  ro,
  sl,
  sk,
  cs,
  pl,
  ca,
  eu,
  gl,
  el,
};

export const FORMATS = {
  ISO: 'yyyy-MM-dd',
  US: 'MM/dd/yyyy',
  EUROPEAN: 'dd/MM/yyyy',
  GERMAN: 'dd.MM.yyyy',
};

export const LOCALIZED_MONTH_DAY_FORMATS = {
  default: 'MMMM do',
  fr: 'd MMMM',
};

export const TIME_FORMATS = {
  TWENTY_FOUR_HOUR: 'HH:mm',
  TWELVE_HOUR: 'h:mm a',
};

/**
 * An object mapping locale codes to their corresponding date formats.
 * The formats are defined in the `FORMATS` object and represent
 * different regional date formatting standards.
 *
 * @constant
 * @type {Object.<string, string>}
 * @property {string} default - The default date format (ISO standard: yyyy-MM-dd).
 * @property {string} en - The US date format (MM/dd/yyyy).
 * @property {string} fr - The European date format (dd/MM/yyyy).
 * @property {string} fr-CH - The German date format used in Switzerland (dd.MM.yyyy).
 * @property {string} it - The European date format (dd/MM/yyyy).
 * @property {string} it-CH - The German date format used in Switzerland (dd.MM.yyyy).
 * @property {string} es - The European date format (dd/MM/yyyy).
 * @property {string} de - The German date format (dd.MM.yyyy).
 * @property {string} pt - The European date format (dd/MM/yyyy).
 * @property {string} pt-BR - The European date format used in Brazil (dd/MM/yyyy).
 * @property {string} hu - The European date format used in Hungary (dd/MM/yyyy).
 * @property {string} hr - The European date format used in Croatia (dd/MM/yyyy).
 * @property {string} sr - The European date format used in Serbia (dd/MM/yyyy).
 * @property {string} ro - The European date format used in Romania (dd/MM/yyyy).
 * @property {string} sl - The European date format used in Slovenia (dd/MM/yyyy).
 * @property {string} sk - The European date format used in Slovakia (dd/MM/yyyy).
 * @property {string} cs - The European date format used in Czech Republic (dd/MM/yyyy).
 * @property {string} pl - The European date format used in Poland (dd/MM/yyyy).
 * @property {string} ca - The European date format used in Catalonia (dd/MM/yyyy).
 * @property {string} eu - The European date format used in Basque Country (dd/MM/yyyy).
 * @property {string} gl - The European date format used in Galicia (dd/MM/yyyy).
 * @property {string} el - The European date format used in Greece (dd/MM/yyyy).
 * @example
 * // Get format for a specific locale
 * const frFormat = localizedFormats.fr; // Returns FORMATS.EUROPEAN
 */
export const localizedFormats = (() => {
  const formatGroups = {
    [FORMATS.US]: ['en'],
    [FORMATS.GERMAN]: ['de', 'fr-CH', 'it-CH'],
    [FORMATS.EUROPEAN]: ['fr', 'it', 'es', 'pt', 'pt-BR', 'hu', 'hr', 'sr', 'ro', 'sl', 'ar', 'sk', 'cs', 'pl', 'ca', 'eu', 'gl', 'el'],
  };

  const formats = { default: FORMATS.ISO };

  Object.entries(formatGroups).forEach(([dateFormat, locales]) => {
    locales.forEach(locale => {
      formats[locale] = dateFormat;
    });
  });

  return formats;
})();

/**
 * List of locale codes that use 12-hour time format (AM/PM) instead of 24-hour format.
 * Most other locales not in this list default to 24-hour format.
 *
 * @constant
 * @type {string[]}
 */
export const localesUsing12Hour = ['en', 'en-US', 'en-GB', 'en-AU', 'en-CA'];

const defaultOpeningHours = () => [
  {
    day: 'MONDAY',
    start: '08:00:00',
    finish: '12:00:00',
  },
  {
    day: 'MONDAY',
    start: '14:00:00',
    finish: '18:00:00',
  },
  {
    day: 'TUESDAY',
    start: '08:00:00',
    finish: '12:00:00',
  },
  {
    day: 'TUESDAY',
    start: '14:00:00',
    finish: '18:00:00',
  },
  {
    day: 'WEDNESDAY',
    start: '08:00:00',
    finish: '12:00:00',
  },
  {
    day: 'WEDNESDAY',
    start: '14:00:00',
    finish: '18:00:00',
  },
  {
    day: 'THURSDAY',
    start: '08:00:00',
    finish: '12:00:00',
  },
  {
    day: 'THURSDAY',
    start: '14:00:00',
    finish: '18:00:00',
  },
  {
    day: 'FRIDAY',
    start: '08:00:00',
    finish: '12:00:00',
  },
  {
    day: 'FRIDAY',
    start: '14:00:00',
    finish: '18:00:00',
  },
];
export const roundToNearestHalfHour = time => {
  const roundTime = roundToNearestMinutes(time, { nearestTo: 30 });

  if (isBefore(roundTime, time)) {
    return addMinutes(roundTime, 30);
  }
  return roundTime;
};

const formatDate = (time, minutes) => setHours(setMinutes(setSeconds(time, 0), minutes % 60), minutes / 60);

const hmsToMinutes = time => reduce((acc, hm) => 60 * acc + Number(hm), 0, init(split(':', time)));

export const convertDaysToMinutes = days => days * 1440;

export const isValidHour = (time, openingHours, endTime) => {
  const currentDay = getDay(time) - 1;
  const morningStartMinutes = hmsToMinutes(openingHours[currentDay * 2].start);
  const morningEndMinutes = hmsToMinutes(openingHours[currentDay * 2].finish);
  const afternoonStartMinutes = hmsToMinutes(openingHours[currentDay * 2 + 1].start);
  const afternoonEndMinutes = hmsToMinutes(openingHours[currentDay * 2 + 1].finish);

  const morningStart = formatDate(time, morningStartMinutes);
  const morningEnd = formatDate(time, morningEndMinutes);
  const afternoonStart = formatDate(time, afternoonStartMinutes);
  const afternoonEnd = formatDate(time, afternoonEndMinutes);

  if (isBefore(time, morningStart)) {
    return false;
  }
  if (isAfter(time, morningEnd) && isBefore(time, afternoonStart)) {
    return false;
  }
  if (isAfter(time, afternoonEnd) || isAfter(time, endTime)) {
    return false;
  }
  return true;
};

export const getStartTime = (time, openingHours, workingDays) => {
  const currentDay = getDay(time) - 1;
  const morningStartMinutes = hmsToMinutes(openingHours[currentDay * 2].start);
  const afternoonStartMinutes = hmsToMinutes(openingHours[currentDay * 2 + 1].start);

  if (getDay(time) === getDay(workingDays[0])) {
    const roundedTime = roundToNearestHalfHour(time);
    if (isAfter(roundedTime, formatDate(time, morningStartMinutes))) {
      return addHours(roundedTime, 2);
    }
    if (isAfter(roundedTime, formatDate(time, afternoonStartMinutes))) {
      return addHours(roundedTime, 2);
    }
  }
  return formatDate(time, morningStartMinutes);
};

export const getEndTime = (time, openingHours, workingDays) => {
  const currentDay = getDay(new Date(time)) - 1;
  const morningEndMinutes = hmsToMinutes(openingHours[currentDay * 2].finish);
  const afternoonEndMinutes = hmsToMinutes(openingHours[currentDay * 2 + 1].finish);

  if (getDay(time) === getDay(last(workingDays))) {
    const roundedTime = roundToNearestHalfHour(time);
    if (isBefore(roundedTime, formatDate(time, morningEndMinutes))) {
      return roundedTime;
    }
    if (isBefore(roundedTime, formatDate(time, afternoonEndMinutes))) {
      return roundedTime;
    }
  }
  return formatDate(time, afternoonEndMinutes);
};

export const includeTimes = (selectedDate, initialOpeningHours = [], workingDays) => {
  if (!selectedDate) {
    return [];
  }
  const openingHours = isEmpty(initialOpeningHours) ? defaultOpeningHours() : initialOpeningHours;
  let time = getStartTime(roundToNearestHalfHour(selectedDate), openingHours, workingDays);
  const times = [];
  const endTime = getEndTime(roundToNearestHalfHour(selectedDate), openingHours, workingDays);

  while (isBefore(time, addMinutes(endTime, 1))) {
    if (isValidHour(time, openingHours, endTime)) {
      times.push(time);
    }
    time = addMinutes(time, 30);
  }
  return times;
};

export const minutesToDays = minutes => minutes / (24 * 60);

export const hoursToDays = hours => hours / 24;

export const hoursToMinutes = hours => hours * 60;

export const daysToMinutes = days => days * 24 * 60;

/**
 * Safely parses a date using date-fns' parseISO and generic new Date() functions.
 * @param {Date|string|number|null|undefined} value - The value be to parsed.
 * @returns {Date} - The newly created date
 */
export const safeDateParse = value => {
  if (value === undefined) {
    return new Date();
  }

  if (value === null || value === '') {
    return new Date('invalid');
  }

  if (value instanceof Date) {
    return isValid(value) ? value : new Date('invalid');
  }

  try {
    if (typeof value === 'string') {
      const parsedIso = parseISO(value);
      if (isValid(parsedIso)) {
        return parsedIso;
      }

      const genericDate = new Date(value);
      if (isValid(genericDate)) {
        return genericDate;
      }

      return new Date('invalid');
    }

    const otherDate = new Date(value);
    return isValid(otherDate) ? otherDate : new Date('invalid');
  } catch {
    return new Date('invalid');
  }
};

/**
 * Retrieves the language setting of the current user's browser.
 * Falls back to `navigator.userLanguage` if `navigator.language` is not available.
 * Uses 'en-US' as default when not in a browser environment.
 *
 * @constant {string} currentUserLanguage - The language code of the user's browser (e.g., "en-US").
 */
export const currentUserLanguage =
  typeof window !== 'undefined' && typeof navigator !== 'undefined' ? navigator.language || navigator.userLanguage : 'en-US';

/**
 * Resolves the locale and corresponding date format based on the user's language settings.
 *
 * @returns {Object} An object containing:
 *   - `locale` {Object}: The resolved locale object.
 *   - `localizedFormat` {string}: The date format string corresponding to the resolved locale.
 */
const resolveLocaleAndFormat = () => {
  const locale = localeMap[currentUserLanguage.split('-')[0]] || enUS;
  const localeKey = Object.keys(localeMap).find(key => localeMap[key] === locale) || 'default';
  const localizedFormat = localizedFormats[localeKey];
  return { locale, localizedFormat };
};

/**
 * Returns the month-day format string for a given locale.
 *
 * @param {string} locale - The locale identifier (e.g., 'en-US', 'fr-FR')
 * @returns {string} The month-day format string for the specified locale, or the default format if the locale is not supported
 */
export const getMonthDayFormat = () => {
  const { locale } = resolveLocaleAndFormat();
  return LOCALIZED_MONTH_DAY_FORMATS[locale.code] || LOCALIZED_MONTH_DAY_FORMATS.default;
};

/**
 * Safely formats a date using date-fns' format function.
 * @param {Date|string|null|undefined} date - The date to format.
 * @param {string} formatString - The format string to use.
 * @param {Object} options - Options to customize the formatting behavior.
 *   @param {Object} [options.locale] - The locale to use for formatting (e.g., enUS).
 *   @param {number} [options.weekStartsOn] - The first day of the week (0 = Sunday, 1 = Monday, etc.).
 *   @param {number} [options.firstWeekContainsDate] - The day of the year used to determine the first week of the year.
 * @param {string} fallback - The fallback value to return if the date is invalid.
 * @returns {string} - The formatted date or the fallback value.
 */
export const safeFormat = (date, formatString, options, fallback = new Date('invalid')) => {
  try {
    const parsedDate = safeDateParse(date);
    if (!isValid(parsedDate)) {
      return String(fallback);
    }

    const { locale } = resolveLocaleAndFormat();
    return format(parsedDate, formatString, {
      ...options,
      locale: options?.locale || locale,
    });
  } catch {
    return String(fallback);
  }
};

/**
 * Determines if the current locale uses 24-hour time format.
 *
 * @returns {boolean} - True if the locale uses 24-hour format, false for 12-hour AM/PM format.
 */
export const uses24HourFormat = () => {
  const userLang = currentUserLanguage.toLowerCase();
  const langCode = userLang.split('-')[0];

  return !localesUsing12Hour.some(locale => locale.toLowerCase() === userLang || locale.toLowerCase() === langCode);
};

/**
 * Returns the appropriate date format string based on the current locale.
 *
 * @returns {string} - The date format string (e.g., 'MM/dd/yyyy', 'dd/MM/yyyy', 'dd.MM.yyyy').
 */
export const getLocalizedDateFormat = () => {
  const { localizedFormat } = resolveLocaleAndFormat();
  return localizedFormat || FORMATS.ISO;
};

/**
 * Returns the appropriate date-time format string based on the locale's time format preference.
 *
 * @param {boolean} includeTime - Whether to include time in the format.
 * @returns {string} - The format string (e.g., 'MM/dd/yyyy h:mm a' for US or 'dd/MM/yyyy HH:mm' for European).
 */
export const getLocalizedDateTimeFormat = (includeTime = true) => {
  const dateFormat = getLocalizedDateFormat();
  if (!includeTime) {
    return dateFormat;
  }

  const timeFormat = uses24HourFormat() ? TIME_FORMATS.TWENTY_FOUR_HOUR : TIME_FORMATS.TWELVE_HOUR;
  return `${dateFormat} ${timeFormat}`;
};
